import React from "react";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { format } from "date-fns";
import type { LocationPhoto } from "../model/location-photo";

interface PhotosBlockProps {
  photos: LocationPhoto[];
  isLoading: boolean;
  onPhotoClick?: (index: number) => void;
}

export const PhotosBlock: React.FC<PhotosBlockProps> = ({ photos, isLoading, onPhotoClick }) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();

  const formatDate = (date: Date | null) => {
    if (!date) return { time: "", date: "" };
    try {
      return {
        time: format(date, "HH:mm"),
        // use specific date representation, optionally with upper cased month char
        date: format(date, "d MMM yyyy", { locale: dateLocale }),
      };
    } catch {
      return { time: "", date: "" };
    }
  };

  const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

  const formatDriverName = (photo: LocationPhoto): string =>
    `${photo.driverFirstName ?? ""} ${photo.driverLastName ?? ""}`.trim() ||
    t(Namespace.PickupLocation, "driverNotSpecified");

  if (isLoading) {
    return (
      <div className="p-6 text-text-secondary">{t(Namespace.PickupLocation, "loadingPhotos")}</div>
    );
  }

  if (!photos || photos.length === 0) {
    return <div className="p-6 text-text-secondary">{t(Namespace.PickupLocation, "noPhotos")}</div>;
  }

  return (
    <div className="w-full flex-1 overflow-auto h-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => {
          const createdAt = fromTimestamp(photo.createdAt);
          const { time, date } = formatDate(createdAt);
          const displayDate = capitalize(date).replace(".", "");
          return (
            <div
              key={String(photo.id)}
              onClick={() => onPhotoClick?.(index)}
              className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
              style={{
                backgroundImage: `url("${photo.url}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* Top-left badge */}
              {createdAt && (
                <div className="absolute top-2 left-2 bg-[#303338]/90 text-white text-xs px-2.5 py-1.5 rounded-lg flex gap-3 font-medium">
                  {time && <span>{time}</span>}
                  {displayDate && <span>{displayDate}</span>}
                </div>
              )}

              {/* Bottom-left badge */}
              <div className="absolute bottom-2 left-2 bg-[#303338]/90 text-white text-xs px-2.5 py-1.5 rounded-lg font-medium">
                {formatDriverName(photo)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
